import { Brain as AgentIcon } from '@tunasong/icons'
import { features, useThunkDispatch } from '@tunasong/redux'
import type { Agent, Persisted } from '@tunasong/schemas'
import { ListItemIcon, ListItemText, Submenu, SubmenuContent, SubmenuTrigger } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { AgentRosterMenuItem } from './agent-roster-item.js'
import type { AgentRoster } from './use-agent-roster.js'

export interface AgentsSubmenuProps {
  /** From `useAgentRoster`; the host owns the hook so it also has the unread count */
  roster: AgentRoster
}

/**
 * The Agents pill as an overflow-menu row, for small screens: "Agents" or "Agents (2 unread)" opens
 * the same per-agent list, and choosing one opens its chat exactly as the pill does. Renders nothing
 * without agents.
 */
export const AgentsSubmenu: FC<AgentsSubmenuProps> = ({ roster }) => {
  const dispatch = useThunkDispatch()

  if (roster.rows.length === 0) {
    return null
  }

  const handleSelect = (agent: Persisted<Agent>) => {
    roster.acknowledge(agent)
    dispatch(features.ui.actions.openAgentComposer({ agentId: agent.id }))
  }

  const label = roster.unreadCount > 0 ? `Agents (${roster.unreadCount} unread)` : 'Agents'

  return (
    <Submenu>
      <SubmenuTrigger>
        <ListItemIcon data-agent-icon aria-hidden="true">
          <AgentIcon />
        </ListItemIcon>
        <ListItemText primary={label} />
      </SubmenuTrigger>
      <SubmenuContent>
        {roster.rows.map(row => (
          <AgentRosterMenuItem key={row.agent.id} row={row} onSelect={handleSelect} />
        ))}
      </SubmenuContent>
    </Submenu>
  )
}
